import { apiCallJson, buildAuthHeaders } from '../utils/auth';

function createAuthorizedRequestInit(method, body) {
  return {
    method,
    headers: buildAuthHeaders(),
    body,
    credentials: 'include'
  };
}

async function parseErrorMessage(response, fallbackMessage = '알 수 없는 오류') {
  const errorData = await response.json().catch(() => ({}));
  return errorData.message || fallbackMessage;
}

async function createDesignImageBlob(dataUrl) {
  const response = await fetch(dataUrl);
  return response.blob();
}

export function buildManualReservationFormData(editData, exchangeRates) {
  const formData = new FormData();

  formData.append('reservation_type', editData.reservation_type || '');
  formData.append('client_name', editData.client_name || '');
  formData.append('client_instagram', editData.client_instagram || '');
  formData.append('client_email', editData.client_email || '');
  formData.append('client_phone', editData.client_phone || '');
  formData.append('min_price', editData.min_price != null ? editData.min_price.toString() : '');
  formData.append('max_price', editData.max_price != null ? editData.max_price.toString() : '');
  formData.append('deposit_amount', editData.deposit_amount != null ? editData.deposit_amount.toString() : '');
  formData.append('deposit_received', editData.deposit_received ? 'true' : 'false');
  formData.append('deposit_received_date', editData.deposit_received_date || '');
  formData.append('deposit_platform', editData.deposit_platform || '');
  formData.append('memo', editData.memo || '');

  if (editData.currency) {
    formData.append('currency', editData.currency);
    if (editData.update_exchange_rate) {
      const marketRate = exchangeRates?.[editData.currency];
      const rate = editData.currency === 'EUR' ? 1 : (marketRate > 0 ? 1 / marketRate : null);
      if (!rate) throw new Error('Exchange rate unavailable');
      formData.append('exchange_rate_to_eur', String(rate));
      formData.append('update_exchange_rate', 'true');
    }
    // Ordinary edits intentionally omit the rate. The server preserves it.
  }

  formData.append('final_payment_received', editData.final_payment_received ? 'true' : 'false');
  formData.append('final_payment_date', editData.final_payment_date || '');
  formData.append('final_payment_platform', editData.final_payment_platform || '');
  formData.append('final_payment_amount', editData.final_payment_amount != null ? editData.final_payment_amount.toString() : '');

  return formData;
}

async function appendDesignImageFields(formData, editData) {
  if (editData.design_image) {
    if (editData.design_image.startsWith('data:image/')) {
      const blob = await createDesignImageBlob(editData.design_image);
      formData.append('design_image', blob, 'design_image.jpg');
    } else {
      formData.append('design_image', editData.design_image);
    }
  } else if (Object.prototype.hasOwnProperty.call(editData, 'design_image')) {
    formData.append('design_image', 'DELETE_IMAGE');
    formData.append('delete_design_image', 'true');
  }

  if (editData.event_id) {
    formData.append('event_id', editData.event_id.toString());
  }
}

export async function fetchManualReservationsForUser({ userId, userLevel }) {
  const queryParams = new URLSearchParams();

  if (userLevel !== 'admin') {
    queryParams.append('artist_id', userId);
  }

  const data = await apiCallJson(`/api/manual-reservations?${queryParams.toString()}`);

  if (!data) {
    return null;
  }

  let manualReservationsData = data.manual_reservations || [];

  if (userLevel !== 'admin') {
    manualReservationsData = manualReservationsData.filter(
      (reservation) => reservation.artist_id === userId
    );
  }

  manualReservationsData.periodTotals = data.period_totals || {};
  return manualReservationsData;
}

export async function deleteManualReservationById(reservationId) {
  const response = await fetch(
    `/api/manual-reservations/${reservationId}`,
    createAuthorizedRequestInit('DELETE')
  );

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response));
  }

  return response.status === 204 ? null : response.json().catch(() => null);
}

export async function deleteManualReservationWithEventById(reservationId) {
  const response = await fetch(
    `/api/manual-reservations/${reservationId}/with-event`,
    createAuthorizedRequestInit('DELETE')
  );

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, '서버 오류가 발생했습니다.'));
  }

  return response.json().catch(() => null);
}

export async function updateManualReservationById(reservationId, editData, exchangeRates) {
  const formData = buildManualReservationFormData(editData, exchangeRates);
  await appendDesignImageFields(formData, editData);

  const response = await fetch(
    `/api/manual-reservations/${reservationId}`,
    createAuthorizedRequestInit('PATCH', formData)
  );

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response));
  }

  return response.json();
}
